import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { useTonAddress, useTonWallet } from '@tonconnect/ui-react';
import {
  gaEnsureEntryPoint,
  gaEntryPoint,
  gaEvent,
  gaSetReferrer,
  gaStoredReferrer,
} from './analytics';
import { ArrowLeft } from 'lucide-react';
import { MaintenanceScreen } from './components/MaintenanceScreen';
import { HeaderV2 } from './components/HeaderV2';
import { NavigationV2, type TabV2 } from './components/NavigationV2';
import { OpenInTelegram } from './components/OpenInTelegram';
import { Lobby } from './components/Lobby';
import { Lottery } from './components/Lottery';
import { Sand } from './components/Sand';
import { SandBackground } from './components/SandBackground';
import { Referrals } from './components/Referrals';
import { Quests } from './components/Quests';
import { JettonInfo } from './components/JettonInfo';
import { ProfileSheet } from './components/ProfileSheet';
import { SidebarV2 } from './components/SidebarV2';
import { StatusStrip } from './components/StatusStrip';
import { usePyramidaState } from './hooks/usePyramidaState';
import { useNotifyLink } from './hooks/useNotifyLink';
import {
  getIncomingReferrerKey,
  getIncomingScreen,
  isTelegramMiniApp,
  getStoryShareKey,
  getStoryShareBot,
  getTelegramStartSource,
} from './ton/referral';
import {
  fetchTelegramReferrer,
  reportAppOpened,
  reportWriteAccessGranted,
  shareReferralStory,
} from './ton/notify';
import { getStoredSub, persistAttribution } from './ton/attribution';
import { reportWebAuth, reportWebClick } from './ton/track';
import { usePartnerLogoutOnDisconnect } from './partner/usePartnerLogoutOnDisconnect';

// The game carries the Three.js engine — keep it out of the main bundle.
const CoinFlip = lazy(() =>
  import('./components/CoinFlip').then((m) => ({ default: m.CoinFlip })),
);
const Rocket = lazy(() =>
  import('./components/Rocket').then((m) => ({ default: m.Rocket })),
);
const Plinko = lazy(() =>
  import('./components/Plinko').then((m) => ({ default: m.Plinko })),
);
const Wheel = lazy(() =>
  import('./components/Wheel').then((m) => ({ default: m.Wheel })),
);

// Maintenance mode: replaces the whole app with a full-screen spinning-pyramid
// splash. Flip to false and redeploy the site to reopen the app.
const MAINTENANCE = false;

type PlayView = 'lobby' | 'lottery' | 'sand' | 'coinflip' | 'rocket' | 'plinko' | 'wheel';

/** Map the bot's `?screen=` deep links onto the v2 three-anchor layout. */
function initialLocation(): { tab: TabV2; play: PlayView; profile: boolean } {
  switch (getIncomingScreen()) {
    case 'lottery':
      return { tab: 'play', play: 'lottery', profile: false };
    case 'sand':
      return { tab: 'play', play: 'sand', profile: false };
    case 'referrals':
      return { tab: 'partner', play: 'lobby', profile: false };
    case 'tasks':
      return { tab: 'tasks', play: 'lobby', profile: false };
    case 'jetton':
      return { tab: 'assets', play: 'lobby', profile: false };
    case 'about':
      return { tab: 'play', play: 'lobby', profile: true };
    default:
      return { tab: 'play', play: 'lobby', profile: false };
  }
}

export function App() {
  const [initial] = useState(initialLocation);
  const [tab, setTab] = useState<TabV2>(initial.tab);

  // Section-level navigation funnel: one event per tab switch.
  useEffect(() => {
    gaEvent('open_tab', { tab });
  }, [tab]);
  const [playView, setPlayView] = useState<PlayView>(initial.play);
  const [profileOpen, setProfileOpen] = useState(initial.profile);
  const [desktopLayout, setDesktopLayout] = useState(
    () => window.matchMedia('(min-width: 1024px)').matches,
  );
  const address = useTonAddress();
  const wallet = useTonWallet();
  const { state } = usePyramidaState(address || null);
  const { t, i18n } = useTranslation();

  // Opt-in: link this wallet to the Telegram notification bot (no-op unless
  // VITE_NOTIFY_API_BASE is configured and running inside Telegram).
  useNotifyLink(address || null, state.me?.registered ?? false);

  useEffect(() => {
    if (!address) return;
    // wallet app distribution: appName + how many messages it can sign at
    // once (drives the multi-ball cap for games like Plinko)
    const device = wallet?.device;
    const send = device?.features?.find(
      (f): f is { name: 'SendTransaction'; maxMessages: number } =>
        typeof f === 'object' && f !== null && f.name === 'SendTransaction' &&
        typeof f.maxMessages === 'number',
    );
    const refKey = getIncomingReferrerKey();
    gaEvent('connect_wallet', {
      ref_key: refKey === null ? (gaStoredReferrer() ?? 'none') : String(refKey),
      wallet_app: device?.appName ?? 'unknown',
      wallet_platform: device?.platform ?? 'unknown',
      max_messages: send ? send.maxMessages : 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  // A deliberate wallet disconnect also ends any partner session while the
  // partner panel's own disconnect handler is not mounted.
  usePartnerLogoutOnDisconnect();

  // Only one TonConnectButton may be mounted at a time (the SDK binds the
  // widget to a single root id) — mirror v1: sidebar on desktop, header on
  // mobile, and the profile sheet takes it over while open.
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setDesktopLayout(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  // Tell the bot the app was opened (drives /start -> first-open conversion and
  // retires the new-user single-CTA /start screen). Best-effort, fires once.
  useEffect(() => {
    // Finalize the first-entry-point classification for launch URLs the
    // index.html boot script found ambiguous (no tgWebApp* marker).
    gaEnsureEntryPoint(isTelegramMiniApp());
    void reportAppOpened();
  }, []);

  // A user who hasn't messaged the bot can't receive its DMs (win alerts, draw
  // reminders). Telegram's native write-access prompt (Bot API 6.9+) fixes
  // that — shown on EVERY launch to any user who hasn't allowed the bot yet,
  // until they grant (`allows_write_to_pm` then arrives true and the prompt
  // stops). Users who came from the bot already have it true — never prompted.
  useEffect(() => {
    const wa = window.Telegram?.WebApp;
    const user = wa?.initDataUnsafe?.user;
    if (!wa?.requestWriteAccess || !user || user.allows_write_to_pm === true) return;
    if (wa.isVersionAtLeast && !wa.isVersionAtLeast('6.9')) return;
    // Triggered by the user's FIRST interaction (tap/click/scroll/key) rather
    // than a timer: the prompt appears exactly when they engage with the app,
    // never over a screen they haven't touched yet.
    // pointerdown/touchstart/wheel/keydown are always user-initiated; a bare
    // 'scroll' is not (the app scrolls programmatically on view changes).
    const EVENTS = ['pointerdown', 'touchstart', 'wheel', 'keydown'] as const;
    const prompt = () => {
      for (const e of EVENTS) window.removeEventListener(e, prompt, true);
      try {
        // A grant is the bot's cue to run its /start flow (welcome + `start`
        // conversion) — it couldn't message the user before this moment.
        wa.requestWriteAccess?.((granted) => {
          if (granted) {
            // Opt-in conversion, segmented by where the user first came from.
            gaEvent('write_access_granted', { entry_point: gaEntryPoint() });
            void reportWriteAccessGranted();
          }
        });
      } catch {
        /* older clients throw on unsupported methods — never break the app */
      }
    };
    for (const e of EVENTS) window.addEventListener(e, prompt, { capture: true, passive: true });
    return () => {
      for (const e of EVENTS) window.removeEventListener(e, prompt, true);
    };
  }, []);

  // Web partner attribution: record the landing (browser visits via a partner
  // `?ref=<key>&sub=…` link) on load, and link the wallet on connect so the
  // indexer can fire the on-chain purchase conversion. No-op inside Telegram.
  useEffect(() => {
    persistAttribution();
    const incoming = getIncomingReferrerKey();
    // Source label: web `?sub=` cookie outside Telegram, the `<key>_<source>`
    // start_param token inside it.
    const sub = getStoredSub() ?? getTelegramStartSource();
    gaSetReferrer(incoming, sub);
    // Bot-driven Mini App opens (menu button / web_app keyboard) carry no
    // start_param: the key the user arrived at the BOT with is only known to
    // the bot, so resolve it there and re-stamp GA once it answers.
    if (incoming === null) {
      void fetchTelegramReferrer().then((key) => {
        if (key !== null) gaSetReferrer(key, sub);
      });
    }
    void reportWebClick();
  }, []);
  useEffect(() => {
    if (address) void reportWebAuth(address);
  }, [address]);

  // Opened from the bot's "share to story" button (`?screen=story&key=<refKey>`):
  // auto-launch the Telegram story composer for that key once the app is ready.
  useEffect(() => {
    const key = getStoryShareKey();
    if (key == null) return;
    const bot = getStoryShareBot();
    void shareReferralStory(
      key,
      {
        caption: bot === 'team' ? t('share.captionTeam') : t('share.caption'),
        widgetName: t('refLink.storyWidget'),
        prizeLabel: t('refLink.storyPrizePool'),
      },
      bot,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the document title / meta description in sync with the active language.
  useEffect(() => {
    document.title = t('meta.title');
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', t('meta.description'));
  }, [t, i18n.language]);

  if (MAINTENANCE) {
    return <MaintenanceScreen />;
  }

  // Countdown badge on the Play anchor: minutes until the current draw.
  const secondsLeft = Math.max(0, state.lottery.roundEndTime - Math.floor(Date.now() / 1000));
  const playBadge =
    tab !== 'play' && secondsLeft > 0 && secondsLeft < 3600
      ? `${Math.max(1, Math.floor(secondsLeft / 60))}m`
      : null;

  const screenKey = tab === 'play' ? `play-${playView}` : tab;
  const subScreen = tab === 'play' && playView !== 'lobby';

  return (
    <div className="app-container visible">
      <SandBackground emitting={tab === 'play' && playView === 'sand'} />
      <div className="app-shell app-shell--v2">
        <SidebarV2
          active={tab}
          renderWalletControl={desktopLayout && !profileOpen}
          onChange={(next) => {
            setTab(next);
            if (next === 'play') setPlayView('lobby');
          }}
          onOpenProfile={() => setProfileOpen(true)}
          onGoHome={() => {
            setTab('play');
            setPlayView('lobby');
          }}
        />
        <div className="app-main">
          <HeaderV2
            renderWalletControl={!desktopLayout && !profileOpen}
            onOpenProfile={() => setProfileOpen(true)}
            onGoHome={() => {
              setTab('play');
              setPlayView('lobby');
            }}
          />
          <main className="app-content">
            {!(tab === 'play' && playView === 'lobby') && (
              <StatusStrip
                state={state}
                onOpen={() => {
                  setTab('play');
                  setPlayView('lottery');
                }}
              />
            )}
            {subScreen &&
              playView !== 'coinflip' &&
              playView !== 'rocket' &&
              playView !== 'plinko' &&
              playView !== 'wheel' && (
              <button
                type="button"
                className="v2-back"
                onClick={() => setPlayView('lobby')}
              >
                <ArrowLeft size={16} aria-hidden="true" /> {t('v2.backToLobby')}
              </button>
            )}
            <AnimatePresence mode="wait">
              <motion.div
                key={screenKey}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              >
                {tab === 'play' && playView === 'lobby' && (
                  <Lobby
                    state={state}
                    address={address || null}
                    onGoLottery={() => {
                      gaEvent('open_game', { game: 'lottery' });
                      setPlayView('lottery');
                    }}
                    onGoSand={() => {
                      gaEvent('open_game', { game: 'sand' });
                      setPlayView('sand');
                    }}
                    onGoCoinFlip={() => {
                      gaEvent('open_game', { game: 'coinflip' });
                      setPlayView('coinflip');
                    }}
                    onGoRocket={() => {
                      gaEvent('open_game', { game: 'rocket' });
                      setPlayView('rocket');
                    }}
                    onGoPlinko={() => {
                      gaEvent('open_game', { game: 'plinko' });
                      setPlayView('plinko');
                    }}
                    onGoWheel={() => {
                      gaEvent('open_game', { game: 'wheel' });
                      setPlayView('wheel');
                    }}
                  />
                )}
                {tab === 'play' && playView === 'lottery' && <Lottery state={state} />}
                {tab === 'play' && playView === 'sand' && <Sand />}
                {tab === 'play' && playView === 'coinflip' && (
                  <Suspense fallback={null}>
                    <CoinFlip onExit={() => setPlayView('lobby')} />
                  </Suspense>
                )}
                {tab === 'play' && playView === 'rocket' && (
                  <Suspense fallback={null}>
                    <Rocket onExit={() => setPlayView('lobby')} />
                  </Suspense>
                )}
                {tab === 'play' && playView === 'plinko' && (
                  <Suspense fallback={null}>
                    <Plinko onExit={() => setPlayView('lobby')} />
                  </Suspense>
                )}
                {tab === 'play' && playView === 'wheel' && (
                  <Suspense fallback={null}>
                    <Wheel onExit={() => setPlayView('lobby')} />
                  </Suspense>
                )}
                {tab === 'assets' && (
                  <JettonInfo
                    state={state}
                    onPlay={() => {
                      setTab('play');
                      setPlayView('lobby');
                    }}
                  />
                )}
                {tab === 'tasks' && (
                  <Quests state={state} onGoPartner={() => setTab('partner')} />
                )}
                {tab === 'partner' && (
                  <Referrals
                    state={state}
                    onGoToLottery={() => {
                      setTab('play');
                      setPlayView('lottery');
                    }}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
        <NavigationV2
          active={tab}
          variant="bottom"
          onChange={(next) => {
            setTab(next);
            if (next === 'play') setPlayView('lobby');
          }}
          playBadge={playBadge}
        />
        <OpenInTelegram />
      </div>
      <ProfileSheet state={state} open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}
